import { EdiSourceDocument } from './edi-viewer';

export const EDI_MAPPING_MODELLER_FEATURE_ID = 'gung-feature-edi.mapping-modeller';
export const EDI_MAPPING_MODELLER_PROTOCOL_VERSION = '1.9.0';

export type EdiTargetFieldKind = 'OBJECT' | 'ARRAY' | 'MAP' | 'ENUM' | 'VALUE';
export type EdiMappingOperation = 'MAP' | 'CONSTANT' | 'DEFAULT' | 'FOR_EACH' | 'WHEN' | 'LOOKUP';
export type EdiMappingTransformType = 'DATE_FORMAT';

export interface EdiMappingTransform {
  type: EdiMappingTransformType;
  inputPattern: string;
  outputPattern: string;
}

export interface EdiTargetField {
  path: string;
  name: string;
  label: string;
  kind: EdiTargetFieldKind;
  valueType: string;
  javaType: string;
  format?: string;
  required: boolean;
  writable: boolean;
  mappable: boolean;
  recursive: boolean;
  enumValues: string[];
  children: EdiTargetField[];
}

export interface EdiTargetContract {
  messageType: string;
  javaType: string;
  contractHash: string;
  fields: EdiTargetField[];
}

export interface EdiTargetFieldSignature {
  path: string;
  kind: EdiTargetFieldKind;
  valueType: string;
  javaType: string;
  format?: string;
}

export interface EdiMappingRule {
  id: string;
  operation: EdiMappingOperation;
  sourceSelector?: string;
  targetPath?: string;
  value?: unknown;
  condition: Record<string, unknown>;
  lookup: Record<string, string>;
  description?: string;
  children: EdiMappingRule[];
  transforms?: EdiMappingTransform[];
}

export interface EdiMappingInstruction {
  schemaVersion: string;
  title: string;
  description?: string;
  direction: 'TO_GUNG';
  source: {
    syntax: string;
    standard?: string;
    version?: string;
    messageType?: string;
  };
  target: {
    messageType: string;
    contractHash: string;
    referencedFields: EdiTargetFieldSignature[];
  };
  rules: EdiMappingRule[];
}

export interface EdiMappingValidation {
  valid: boolean;
  errors: string[];
  warnings: string[];
}

export type EdiMappingRuleStatus = 'APPLIED' | 'SKIPPED' | 'FAILED';

export interface EdiMappingRuleResult {
  ruleId: string;
  operation: EdiMappingOperation;
  sourceSelector?: string;
  targetPath?: string;
  status: EdiMappingRuleStatus;
  matchedSourceNodes: number;
  writtenValues: number;
  message: string;
}

export interface EdiMappingDryRun {
  successful: boolean;
  runtimeTyped: boolean;
  validation: EdiMappingValidation;
  result?: unknown;
  ruleResults: EdiMappingRuleResult[];
  errors: string[];
  warnings: string[];
}

export interface EdiMappingDryRunResult {
  fileName: string;
  dryRun: EdiMappingDryRun;
}

export interface EdiMappingDryRunBatch {
  total: number;
  passed: number;
  failed: number;
  results: EdiMappingDryRunResult[];
}

export interface EdiMappingModel {
  source: EdiSourceDocument;
  target: EdiTargetContract;
  instruction: EdiMappingInstruction;
  validation: EdiMappingValidation;
  instructionOrigin: string;
  warnings: string[];
}

export interface EdiMappingModelSample {
  sourceId: string;
  fileName: string;
  mediaType?: string;
  syntax: string;
  standard?: string;
  version?: string;
  messageType?: string;
  rawContent: string;
}

export interface EdiMappingSourceDocument {
  id: string;
  modelId: string;
  fileName: string;
  mediaType?: string;
  sizeBytes: number;
  sha256: string;
  syntax: string;
  standard?: string;
  version?: string;
  messageType?: string;
  rawContent: string;
  createdAt: string;
  createdBy: string;
}

export interface EdiMappingPrompt {
  text: string;
  user: string;
  recordedAt: string;
  modelVersion: number;
}

export interface EdiSavedMappingModelSummary {
  modelId: string;
  name: string;
  version: number;
  targetMessageType: string;
  sourceSyntax: string;
  sourceStandard?: string;
  sourceVersion?: string;
  sourceMessageType?: string;
  sourceFileName?: string;
  sourceCount: number;
  promptCount: number;
  createdAt: string;
  createdBy: string;
  modifiedAt: string;
  modifiedBy: string;
}

export interface EdiSavedMappingModel {
  id: string;
  modelId: string;
  name: string;
  version: number;
  parentVersionId?: string;
  targetMessageType: string;
  instruction: EdiMappingInstruction;
  sample: EdiMappingModelSample;
  sourceDocumentIds: string[];
  primarySourceDocumentId?: string;
  sources: EdiMappingSourceDocument[];
  validation: EdiMappingValidation;
  prompts: EdiMappingPrompt[];
  createdAt: string;
  createdBy: string;
  modifiedAt: string;
  modifiedBy: string;
}
